import { useEffect, useMemo, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { Welcome } from './components/Welcome';
import { Questionnaire } from './components/Questionnaire';
import { RecipeCard } from './components/RecipeCard';
import { ReturnLink } from './components/ReturnLink';
import { generatePlan } from './lib/generate';
import type { AftercareInputs, SavedRecipe } from './data/types';
import { DEFAULT_INPUTS } from './data/types';
import styles from './App.module.css';

type Phase = 'welcome' | 'questionnaire' | 'card';

const RECIPES_KEY = 'aftercare-recipe:saved';

function newId() {
  return `${Date.now()}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

export function App() {
  const [phase, setPhase] = useState<Phase>('welcome');
  const [inputs, setInputs] = useState<AftercareInputs>(DEFAULT_INPUTS);
  const [saved, setSaved] = useState<SavedRecipe[]>(() =>
    storage.get<SavedRecipe[]>(RECIPES_KEY, [])
  );
  const [currentSavedId, setCurrentSavedId] = useState<string | null>(null);

  // Persist saved list
  useEffect(() => {
    storage.set(RECIPES_KEY, saved);
  }, [saved]);

  const plan = useMemo(() => generatePlan(inputs), [inputs]);
  const isSaved = currentSavedId !== null && saved.some((r) => r.id === currentSavedId);

  function begin() {
    setInputs(DEFAULT_INPUTS);
    setCurrentSavedId(null);
    setPhase('questionnaire');
  }

  function loadRecipe(id: string) {
    const found = saved.find((r) => r.id === id);
    if (!found) return;
    setInputs(found.inputs);
    setCurrentSavedId(found.id);
    setPhase('card');
  }

  function deleteRecipe(id: string) {
    setSaved((s) => s.filter((r) => r.id !== id));
    if (currentSavedId === id) {
      setCurrentSavedId(null);
      setPhase('welcome');
    }
  }

  function saveCurrent() {
    const id = currentSavedId ?? newId();
    const record: SavedRecipe = {
      id,
      name: inputs.recipeName?.trim() || plan.title,
      inputs,
      plan,
      createdAt: new Date().toISOString(),
    };
    setSaved((s) => {
      const idx = s.findIndex((r) => r.id === id);
      return idx >= 0 ? [...s.slice(0, idx), record, ...s.slice(idx + 1)] : [record, ...s];
    });
    setCurrentSavedId(id);
  }

  function makeAnother() {
    begin();
  }

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
        {phase !== 'welcome' && (
          <button
            type="button"
            className={styles.backHome}
            onClick={() => setPhase('welcome')}
          >
            ← my recipes
          </button>
        )}
      </header>
      <main className={styles.stage}>
        {phase === 'welcome' && (
          <Welcome
            saved={saved}
            onBegin={begin}
            onLoad={loadRecipe}
            onDelete={deleteRecipe}
          />
        )}
        {phase === 'questionnaire' && (
          <Questionnaire
            inputs={inputs}
            onChange={setInputs}
            onSubmit={() => setPhase('card')}
          />
        )}
        {phase === 'card' && (
          <RecipeCard
            plan={plan}
            saved={isSaved}
            onSave={saveCurrent}
            onEdit={() => setPhase('questionnaire')}
            onAnother={makeAnother}
          />
        )}
      </main>
      <footer className={styles.footer}>
        <p>aftercare is care, not medical advice. trust your body.</p>
      </footer>
    </>
  );
}
